import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import styles from './ForgotPassword.module.css';
import { Button, LabeledFormField, Notify } from 'components/core';
import { RouteConstants } from '../../../constants';
import { usePasswordService, useUserService } from 'services';
import { ApiError } from 'helpers';

type TokenState = 'validating' | 'valid' | 'invalid';

interface IFormValues {
  newPassword: string;
  confirmPassword: string;
}

const CheckingLinkCard = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={`h4 ${styles.title}`}>{t('AUTH.CHECKING_LINK_TITLE')}</h1>
        <p className={styles.subtitle}>{t('AUTH.CHECKING_LINK_MESSAGE')}</p>
      </div>
    </div>
  );
};

interface InvalidLinkCardProps {
  reason: string | null;
}

const InvalidLinkCard = ({ reason }: InvalidLinkCardProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={`h4 ${styles.title}`}>{t('AUTH.LINK_INVALID_TITLE')}</h1>
        <div className={styles.confirmationBoxError}>
          <span className={`material-icons ${styles.confirmationIcon}`}>error_outline</span>
          <span>{reason ?? t('AUTH.LINK_INVALID_MESSAGE_FALLBACK')}</span>
        </div>
        <Link to={RouteConstants.forgotPassword} className={styles.backToLogin}>
          {t('AUTH.REQUEST_NEW_LINK')}
        </Link>
      </div>
    </div>
  );
};

interface ResetFormCardProps {
  isSubmitted: boolean;
  onSubmit: (values: IFormValues) => Promise<void>;
}

// Built from `t` inside the component (memoized on `t`) rather than as a
// module-level constant, which would freeze error messages in whatever
// language was active at load time.
const createResetPasswordValidationSchema = (t: TFunction) =>
  Yup.object().shape({
    newPassword: Yup.string()
      .required(t('AUTH.NEW_PASSWORD_REQUIRED'))
      .min(8, t('AUTH.PASSWORD_MIN_LENGTH')),
    confirmPassword: Yup.string()
      .required(t('AUTH.CONFIRM_PASSWORD_REQUIRED'))
      .oneOf([Yup.ref('newPassword')], t('AUTH.PASSWORD_MISMATCH')),
  });

const ResetFormCard = ({ isSubmitted, onSubmit }: ResetFormCardProps) => {
  const { t } = useTranslation();
  const resetPasswordValidationSchema = useMemo(() => createResetPasswordValidationSchema(t), [t]);
  const { register, handleSubmit, formState } = useForm<IFormValues>({
    resolver: yupResolver(resetPasswordValidationSchema),
  });
  const { errors, isSubmitting } = formState;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {isSubmitted ? (
          <>
            <h1 className={`h4 ${styles.title}`}>{t('AUTH.RESET_PASSWORD_SUCCESS_TITLE')}</h1>
            <div className={styles.confirmationBox}>
              <span className={`material-icons ${styles.confirmationIcon}`}>check_circle</span>
              <span>{t('AUTH.RESET_PASSWORD_SUCCESS_MESSAGE')}</span>
            </div>
          </>
        ) : (
          <>
            <h1 className={`h4 ${styles.title}`}>{t('AUTH.RESET_PASSWORD_TITLE')}</h1>
            <p className={styles.subtitle}>{t('AUTH.RESET_PASSWORD_SUBTITLE')}</p>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <LabeledFormField
                label={t('AUTH.RESET_NEW_PASSWORD_LABEL')}
                icon="lock"
                type="password"
                placeholder={t('AUTH.RESET_NEW_PASSWORD_PLACEHOLDER')}
                required
                autoFocus
                error={errors.newPassword?.message}
                registration={register('newPassword')}
              />

              <LabeledFormField
                label={t('AUTH.RESET_CONFIRM_PASSWORD_LABEL')}
                icon="lock"
                type="password"
                placeholder={t('AUTH.RESET_CONFIRM_PASSWORD_PLACEHOLDER')}
                required
                error={errors.confirmPassword?.message}
                registration={register('confirmPassword')}
              />

              <Button className="w-100" disabled={isSubmitting}>
                {isSubmitting && <span className="spinner-border spinner-border-sm me-3"></span>}
                {isSubmitting ? t('AUTH.RESETTING_PASSWORD') : t('AUTH.RESET_PASSWORD_BUTTON')}
              </Button>
            </form>
          </>
        )}

        {!isSubmitted && (
          <Link to={RouteConstants.login} className={styles.backToLogin}>
            {t('AUTH.BACK_TO_LOGIN')}
          </Link>
        )}
      </div>
    </div>
  );
};

export const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const passwordService = usePasswordService();
  const { logout } = useUserService();
  const token = searchParams.get('token')?.trim() ?? '';

  const [tokenState, setTokenState] = useState<TokenState>('validating');
  const [invalidReason, setInvalidReason] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalidReason(null);
      setTokenState('invalid');
      return;
    }

    let isMounted = true;
    passwordService
      .validateResetToken(token)
      .then(() => {
        if (isMounted) setTokenState('valid');
      })
      .catch((message: ApiError) => {
        if (isMounted) {
          setInvalidReason(message || t('AUTH.LINK_INVALID_MESSAGE_FALLBACK'));
          setTokenState('invalid');
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = useCallback(
    (values: IFormValues) => {
      return passwordService
        .resetPassword(token, values.newPassword, values.confirmPassword)
        .then(() => {
          setIsSubmitted(true);
          // A password reset must always land on a fresh login, never silently continue
          // whatever session (possibly a completely different account) was already active
          // in this browser - logout() clears both localStorage and the in-memory auth
          // state before navigating, which a plain navigate(RouteConstants.login) does not.
          setTimeout(() => {
            logout();
          }, 2500);
        })
        .catch((message: ApiError) => Notify.error(message || t('AUTH.RESET_PASSWORD_FAILED_FALLBACK')));
    },
    [passwordService, token, logout, t],
  );

  if (tokenState === 'validating') return <CheckingLinkCard />;
  if (tokenState === 'invalid') return <InvalidLinkCard reason={invalidReason} />;
  return <ResetFormCard isSubmitted={isSubmitted} onSubmit={onSubmit} />;
};
